<template>
  <div class="flex flex-col flex-grow h-full relative">
    <div class="flex flex-wrap gap-y-1 gap-2 w-full p-3">
      <ui-category-badge
        v-if="user?.is_admin"
        svg="fa fa-arrow-left !text-fakeblack !text-white"
        :text="t('supportchat.chatwindow.leave')"
        class="cursor-pointer !bg-slate-600 !text-white"
        icon-left
        @click="methodLeave()"
        />

      <ui-category-badge
        svg="fa fa-arrow-left !text-fakeblack"
        :text="t('supportchat.chatwindow.finish')"
        class="cursor-pointer flex-grow truncate !bg-red-200"
        icon-left
        @click="methodFinishChatSession()"
        />

      <ui-category-badge
        v-if="dataUsersPresent > 0 && user?.is_admin"
        svg="fa fa-user-friends !text-fakeblack"
        class="!bg-orange-200"
        :text="dataUsersPresent+''"
        icon-left
        />
    </div>

    <div
      ref="refScrollChat"
      class="bg-slate-100 border-y border-slate-200 w-full h-[500px] max-h-[40vh] flex flex-grow flex-col p-4 gap-y-2 overflow-auto"
      >
      <UiChatMessage
        v-for="(msg,i) in dataChatMessages"
        :key="msg.id"
        :sent-by-me="msg.sentByMe"
        :message="msg.message"
        :created-at="msg.created_at"
        :avatar_url="msg.avatar_url"
        :sendername="msg.sendername"
        :is-last-same-sender="i > 0 && dataChatMessages[i-1] && dataChatMessages[i-1].sendername === msg.sendername"
        :is-next-same-sender="i < dataChatMessages.length - 1 && dataChatMessages[i+1] && dataChatMessages[i+1].sendername === msg.sendername"
        />

      <UniqueSupportTypings v-model="dataTypings" />

      <div
        v-if="dataIsFinished"
        class="ui-banner bg-green-200 sticky bottom-0"
        v-text="t('supportchat.chatwindow.finished')"
        />
    </div>

    <div class="p-4 flex gap-2">
      <UiText
        v-model="message"
        :placeholder="t('supportchat.chatwindow.messageplaceholder')"
        rows="1"
        @keydown="methodSendSupportMessage"
        />
      <ui-button
        svg="fa fa-paper-plane"
        class="dark"
        @click="methodSendSupportMessage(null)"
        />
    </div>
  </div>
</template>

<script setup lang="ts">
const emit = defineEmits([
  'new-message',
  'finished'
])

const { t } = useI18n()
const refScrollChat = ref<HTMLElement|null>(null)

const message = ref<string>('')
const modelSession = defineModel<string|null>('session')
const modelPopup = defineModel<boolean>('popup')

const dataIsFinished = ref(false)
const dataUsersPresent = ref<number>(0)
const dataChatMessages = ref<ChatMessages>([])
const dataTypings = ref<ChatTyping[]>([])

onMounted(() => {
  methodLoadSession()
  methodSetupListener()
})

onBeforeUnmount(() => {
  ws.unsubscribe(computedChannelName.value)
})

const methodSendSupportMessage = (key: KeyboardEvent|null) => {
  if(key !== null){
    if(key.key === 'Enter' && key.shiftKey){
      console.log('Shift + Enter')
      // insert newline at cursor position
      const start = (key.target as HTMLTextAreaElement).selectionStart
      const end = (key.target as HTMLTextAreaElement).selectionEnd
      message.value = message.value.substring(0, start) + '\n' + message.value.substring(end, message.value.length)

      // Set cursor to after newline
      nextTick(() => {
        (key.target as HTMLTextAreaElement).selectionStart = start + 1;
        (key.target as HTMLTextAreaElement).selectionEnd = start + 1;
      })


      key.preventDefault()
      return
    }

    if (key.key !== 'Enter'){
      return
    }
  }

  if(key)
    key.preventDefault()

  if(message.value.trim() === '' || !modelSession.value){
    return
  }

  ws.send('support.send',{
    session: modelSession.value,
    visitor_id: visitorId(),
    message: message.value
  }, computedChannelName.value).then((r) => {
    dataChatMessages.value.push({
      id: r.chat.id,
      message: r.chat.message,
      sentByMe: true,
      created_at: r.chat.created_at,
    })
  })

  typingIndicator(true)

  message.value = ''
}

const methodSetupListener = () => {
  if (!modelSession.value){
    return
  }

  // === New Message
  ws.listenWhileMounted<{
    message: string,
    type: string,
    created_at: string,
    sender: string
  }>('info:new_message',computedChannelName.value, (data: any) => {
    dataChatMessages.value.push({
      id: data.chat.id,
      message: data.chat?.message,
      sentByMe: data.sender === 'visitor',
      created_at: data.chat?.created_at,
      avatar_url: data.chat?.avatar_url,
      sendername: data.chat?.sendername,
    })

    emit('new-message', data.chat)
  })

  // === Finished
  ws.listenWhileMounted('info:chat_finished',computedChannelName.value, () => {
    emit('finished', modelSession.value)
    dataIsFinished.value = true
  })

  // === Presence changed
  ws.listenWhileMounted<{
    removed: boolean,
    joined: boolean,
    sockets: string[],
    total_count: number
  }>('presence.changed',computedChannelName.value, (data: any) => {
    dataUsersPresent.value = data.total_count
  })

  // === Typing
  ws.listenWhileMounted<{
    avatar_url: string,
    sendername: string
    typer_id: string
  }>('info:typing',computedChannelName.value, (data: any) => {
    const existingTyping = dataTypings.value.find(t => t.typer_id === data.typer_id)

    if (existingTyping){
      existingTyping.lasttyping = data.finished
        ? 0
        : Date.now()
    } else {
      dataTypings.value.push({
        typer_id: data.typer_id,
        sendername: data.sendername,
        avatar_url: data.avatar_url,
        lasttyping: Date.now()
      })
    }

    dataTypings.value = dataTypings.value.filter(t => (Date.now() - t.lasttyping) < 1500)

    // Remove after 5 seconds
    setTimeout(() => {
      dataTypings.value = dataTypings.value.filter(t => (Date.now() - t.lasttyping) < 1500)
    }, 1520)
  })

}

const methodLoadSession = () => {
  if (!modelSession.value){
    return
  }

  ws.send('support.session',{
    session: modelSession.value,
    visitor_id: visitorId()
  }, computedChannelName.value).then((r) => {
    dataChatMessages.value = r.chat
  })
}

const methodFinishChatSession = () => {
  if (!modelSession.value){
    return
  }

  ws.send('support.finish',{
    session: modelSession.value,
    visitor_id: visitorId()
  }, computedChannelName.value)
    .catch(api.parseError)
    .finally(methodLeave)
}

const methodLeave = () => {
  ws.unsubscribe(computedChannelName.value)

  if(!((user.value as User)?.is_admin ?? false)){
    modelPopup.value = false
  }

  nextTick(() => {
    modelSession.value = null
    dataChatMessages.value = []
    message.value = ''
  })
}

const computedChannelName = computed(() => {
  return 'openpresence-websocket.support-'+modelSession.value
})

const methodScrollDown = () => {
  nextTick(() => {
    if (refScrollChat.value){
      refScrollChat.value.scrollTop = refScrollChat.value.scrollHeight
    }
  })

  setTimeout(() => {
    if (refScrollChat.value){
      refScrollChat.value.scrollTop = refScrollChat.value.scrollHeight
    }
  }, 100)
}


const typingIndicator = (finished = false) => {
  console.log('Typing indicator')
  ws.send('support.typing',{
    session: modelSession.value,
    visitor_id: visitorId(),
    finished
  }, computedChannelName.value).catch(() => {})
}

const bounceTypingIndicator = useDebounce(typingIndicator, 800,{
  leading: true,
  trailing: false,
  maxWait: 800
})

watch(modelSession, methodLoadSession)
watch(() => [
  dataChatMessages.value?.length,
  dataIsFinished.value,
  dataTypings.value?.length
], methodScrollDown)
watch(() => message.value?.length, (v) => {v ? bounceTypingIndicator() : []})

defineExpose({
  methodScrollDown
})
</script>
